if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.productIds = [...new Set(this.dataset.productIds.split(',').filter(Boolean))];
        this.limit = Number(this.dataset.limit) || 6;
        this.currency = this.dataset.currency || 'USD';
        this.carousel = this.querySelector('[data-recommendations-carousel]');
        this.status = this.querySelector('[data-recommendations-status]');
        this.currentIndex = 0;

        if (!this.productIds.length || !this.carousel) return;
        this.loadRecommendations();
      }

      async loadRecommendations() {
        try {
          const productLists = await Promise.all(
            this.productIds.slice(0, 4).map(async (productId) => {
              const complementary = await this.fetchRecommendations(productId, 'complementary');
              if (complementary.length) return complementary;
              return this.fetchRecommendations(productId, 'related');
            })
          );
          const seen = new Set(this.productIds.map(String));
          this.products = productLists
            .flat()
            .filter((product) => {
              const id = String(product.id);
              if (seen.has(id) || !product.available) return false;
              seen.add(id);
              return true;
            })
            .slice(0, this.limit);

          if (!this.products.length) return;
          this.hidden = false;
          this.render();
        } catch (error) {
          this.hidden = true;
        }
      }

      async fetchRecommendations(productId, intent) {
        const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=6&intent=${intent}`;
        const response = await fetch(url);
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data.products) ? data.products : [];
      }

      render() {
        const product = this.products[this.currentIndex];
        const variants = product.variants.filter((variant) => variant.available);
        if (!variants.length) {
          this.products.splice(this.currentIndex, 1);
          this.currentIndex = Math.min(this.currentIndex, this.products.length - 1);
          if (this.products.length) this.render();
          else this.hidden = true;
          return;
        }

        const image = this.imageUrl(product.featured_image || product.images[0]);
        const position = `${this.currentIndex + 1} / ${this.products.length}`;
        this.carousel.innerHTML = `
          <button class="cart-drawer__recommendations-arrow" type="button" data-recommendations-previous aria-label="${this.escapeHtml(this.dataset.previousLabel)}" ${this.products.length < 2 ? 'disabled' : ''}>
            <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m15 18-6-6 6-6"/></svg>
          </button>
          <article class="cart-drawer__recommendation-card" aria-label="${this.escapeHtml(position)}">
            <a class="cart-drawer__recommendation-image-link" href="${this.escapeHtml(product.url)}" tabindex="-1">
              ${image ? `<img class="cart-drawer__recommendation-image" src="${this.escapeHtml(image)}" alt="${this.escapeHtml(product.title)}" width="120" height="140" loading="lazy">` : ''}
            </a>
            <div class="cart-drawer__recommendation-content">
              <a class="cart-drawer__recommendation-title" href="${this.escapeHtml(product.url)}">${this.escapeHtml(product.title)}</a>
              <div class="cart-drawer__recommendation-prices" data-recommendation-prices></div>
              <div class="cart-drawer__recommendation-actions">
                <select class="cart-drawer__recommendation-select" data-recommendation-variant aria-label="${this.escapeHtml(product.title)}">
                  ${variants.map((variant) => `<option value="${variant.id}" data-price="${variant.price}" data-compare-at-price="${variant.compare_at_price || 0}">${this.escapeHtml(variant.public_title || variant.title)}</option>`).join('')}
                </select>
                <button class="cart-drawer__recommendation-add" type="button" data-recommendation-add>${this.escapeHtml(this.dataset.addLabel)}</button>
              </div>
            </div>
          </article>
          <button class="cart-drawer__recommendations-arrow" type="button" data-recommendations-next aria-label="${this.escapeHtml(this.dataset.nextLabel)}" ${this.products.length < 2 ? 'disabled' : ''}>
            <svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        `;

        this.variantSelect = this.querySelector('[data-recommendation-variant]');
        this.addButton = this.querySelector('[data-recommendation-add]');
        this.updatePrices();
        this.variantSelect.addEventListener('change', () => this.updatePrices());
        this.addButton.addEventListener('click', () => this.addToCart());
        this.querySelector('[data-recommendations-previous]').addEventListener('click', () => this.move(-1));
        this.querySelector('[data-recommendations-next]').addEventListener('click', () => this.move(1));
      }

      move(direction) {
        this.currentIndex = (this.currentIndex + direction + this.products.length) % this.products.length;
        this.render();
      }

      updatePrices() {
        const option = this.variantSelect.selectedOptions[0];
        const price = Number(option.dataset.price);
        const compareAtPrice = Number(option.dataset.compareAtPrice);
        const prices = this.querySelector('[data-recommendation-prices]');
        prices.innerHTML = `${compareAtPrice > price ? `<s class="cart-drawer__recommendation-compare">${this.formatMoney(compareAtPrice)}</s>` : ''}<span class="cart-drawer__recommendation-price">${this.formatMoney(price)}</span>`;
      }

      async addToCart() {
        if (this.addButton.getAttribute('aria-disabled') === 'true') return;
        this.addButton.setAttribute('aria-disabled', 'true');
        this.addButton.textContent = this.dataset.addingLabel;
        const variantId = Number(this.variantSelect.value);
        const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        const formData = new FormData();
        formData.append('id', String(variantId));
        formData.append('quantity', '1');

        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const state = await response.json();
          if (!response.ok || state.status) throw new Error(state.description || this.dataset.errorLabel);

          this.status.textContent = this.dataset.addedLabel;
          const refreshedState = await this.refreshCartSections(state, root);
          const cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer || !refreshedState.sections || !refreshedState.sections['cart-drawer']) {
            window.location.reload();
            return;
          }
          cartDrawer.renderContents(refreshedState);
        } catch (error) {
          this.status.textContent = this.dataset.errorLabel;
          this.addButton.removeAttribute('aria-disabled');
          this.addButton.textContent = this.dataset.addLabel;
        }
      }

      async refreshCartSections(state, root) {
        try {
          const response = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) return state;
          const sections = await response.json();
          if (!sections['cart-drawer'] || !sections['cart-icon-bubble']) return state;
          return { ...state, sections };
        } catch (error) {
          return state;
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      }

      imageUrl(image) {
        if (!image) return '';
        const source = typeof image === 'string' ? image : image.src;
        if (!source) return '';
        return `${source}${source.includes('?') ? '&' : '?'}width=240`;
      }

      escapeHtml(value) {
        return String(value || '')
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#039;');
      }
    }
  );
}
